export const ACTION_TYPES = [
  // A source event with no browser operation. Captured-session journeys use this to preserve and compare the
  // DOM/a11y checkpoint for every event in order, including events that cannot or should not be replayed.
  'checkpoint',
  'navigate',
  // Back-navigate the browser to the previous page (history.back). Takes NO selector/value — like
  // scrollToBottom/scrollToTop it acts on the page globally. Used to retreat from a dead end (e.g. a
  // click that redirected into a login/auth wall) and continue exploring elsewhere.
  'back',
  'click',
  // Hover the pointer over a target element and let the page settle — WITHOUT clicking. This
  // triggers the CSS `:hover` pseudo-class (and any JS hover handlers) so hover-driven UI changes
  // (tooltips, popovers, layout shifts from :hover rules that resize/reposition the element or its
  // siblings) are captured in a settled recording frame. A `click` action does call `.hover()` first,
  // but the immediately-following click changes page state and the transient :hover layout is never
  // captured on its own; a dedicated `hover` step lets the hovered state persist so downstream
  // layout-shift / glitches detectors can flag it. `selector` targets the element to hover.
  'hover',
  // Sweep the pointer across a target element in multiple steps, generating a series of
  // mousemove events along the path — NOT a single hover-to-center. A `hover` action moves
  // the pointer to the element's center and stops (at most one mousemove event), which never
  // exercises a mousemove handler that writes cursor position into layout-affecting styles on
  // each cursor update. `mousemove` reuses the drag stepping pattern (page.mouse.move() in
  // ~12 steps across the element's bounding box) WITHOUT mouse.down()/mouse.up(), so a
  // mousemove-driven reflow / cursor-tracking interaction is captured in the recording.
  // `selector` targets the element to sweep across.
  'mousemove',
  'type',
  'input',
  // Press one or more keyboard keys, optionally after focusing `selector` first (the same
  // selector-focus behavior as `type`/`input`, so the keypress lands in the intended field
  // rather than whatever last had focus). `value` is a SEQUENCE of one or more Playwright
  // key names separated by whitespace, pressed IN ORDER — each token is either a single key
  // ('Enter', 'Delete', 'Backspace', 'ArrowDown', 'Tab') or a modifier chord joined with '+'
  // ('Control+A', 'Shift+Tab'). This is how a compound keyboard operation is authored in ONE
  // step: e.g. clear a text field with value 'Control+A Delete' (select-all, then delete the
  // selection) — the real gesture a user performs. NEVER model clearing a field as a `type`
  // with an empty value (that appends nothing and never clears). Runner OS is Linux, so use
  // 'Control+…' modifiers (not 'Meta+…'). Defaults to 'Enter' when `value` is omitted.
  'pressKey',
  'select',
  'drag',
  'dragTo',
  // UPLOAD a file. `value` is the SLUG of a file from the global test-file pool (comma-separate several
  // for an input that accepts multiple); `selector`/`label` targets either the `<input type="file">`
  // itself or the visible control that opens the file dialog — an "Attach file" button, a paperclip, an
  // "Upload photo" tile. A file input is set directly (which works even when it is hidden, as it usually
  // is); anything else is CLICKED with the chosen file armed, so the dialog Playwright intercepts is
  // answered with that file. The step FAILS, naming what happened, when the clicked control opens no
  // dialog and sets no input — that is the genuine "this button does not reach a file picker" defect, and
  // the distinction matters: an intercepted dialog is invisible by design, so its absence from a
  // screenshot is not evidence (#1362).
  'uploadFile',
  // DROP a file on a dropzone, as a user would from their desktop. `value` is the pool slug(s),
  // `selector`/`label` the drop target. Playwright cannot drop an OS file, so the DataTransfer is built
  // inside the page and dragenter/dragover/drop are dispatched with it (see dropFilesOnTarget). This is
  // the ONLY way to exercise a dropzone-only uploader — one that wires no file input has nothing for
  // `uploadFile` to click or set.
  'dropFile',
  'read_variable',
  // Scroll the whole page AND every independent scroll container to the bottom ('scrollToBottom')
  // or top ('scrollToTop'). Neither takes a selector or value — they act on the page globally,
  // walking every scrollable element (an overflow-y auto/scroll/overlay box taller than its
  // client height, e.g. a full-height side rail) so bottom-docked / below-the-fold chrome that
  // exposes no click/fill interaction (account/user identity footers, sticky totals) is brought
  // into a settled, in-viewport frame the recording captures. A downstream pass gated on an
  // in-viewport screenshot can only confirm a below-fold defect once such a frame exists.
  'scrollToBottom',
  'scrollToTop',
  // Scroll the page and every scroll container by ONE viewport down/up (with a small overlap so a
  // fold-straddling control is not skipped). Unlike scrollToBottom/scrollToTop, which jump to the
  // extremes, these page THROUGH a long document to reach controls in the middle. No selector/value.
  'scrollDown',
  'scrollUp',
  // An ABSTRACT step: `action` is an English description of intent rather than a concrete
  // interaction. The runner does NOT execute it through executeAction — it routes the step to its
  // handleAbstractAction handler, which asks the model to pick a concrete action (or file a bug)
  // against the live page. executeAction has a defensive no-op case so a stray abstract step that
  // reaches it never throws. The journey generator may author abstract steps against this contract.
  'abstract',
  // An ABSTRACT ACTION SEQUENCE step: `action` is an English GOAL, and the runner drives the page
  // toward it with a bounded observe→act→observe LOOP (its abstractActionSequence utility) — the
  // MULTI-action sibling of 'abstract' (which resolves to a single action). Like 'abstract' it is NOT
  // run through executeAction; the runner intercepts it and performs one model-chosen action per turn
  // until the model reports the goal reached (done) or genuinely unreachable (fail), or an action
  // budget is exhausted. Typical use: a leading step that navigates to — and, if the app gates it,
  // logs into — the page a journey needs before its concrete steps run. executeAction has a defensive
  // no-op case so a stray one never throws.
  'abstractActionSequence',
  // Logical project-surface reference. The runner expands it before execution.
  'reachSurface',
  'callTool',
  // AWAIT / RECEIVE AN EMAIL. `action` is an English description of the email to expect and what to do
  // with it (e.g. "wait for the signup verification email and read the 6-digit code" or "open the magic
  // sign-in link in the login email"). `value` is an optional search term (subject/body/sender) that
  // filters which received email to use; `variable`, when set, receives a value the model extracts from
  // the email body (a code, an order number, …) for later steps to type. Like 'abstract' this is NOT run
  // through executeAction — the runner intercepts it, polls the project email inbox (shared inbox util),
  // hands the RAW matching email to a bounded model turn that either stores `variable` or opens one of
  // the email's links BY REFERENCE (the runner navigates the real URL — a tokenised magic-link URL is
  // never transcribed), and FAILS the step (→ bug) if no matching email arrives within the poll window.
  // executeAction has a defensive no-op case so a stray one never throws.
  'awaitEmail',
] as const
export type ActionType = (typeof ACTION_TYPES)[number]

/**
 * Map a model-authored action verb onto the runner's vocabulary. A model asked to enter text into a
 * field often names the intent as the verb — "fill the email field" — but the runner has no `fill`: it
 * types with `type`/`input`. An unmapped verb falls through the executor's switch as a silent no-op and
 * is recorded into learned tool revisions, where the persisted action schema rejects it, so every
 * model-authored action passes through this at its conversion boundary (journey authoring's step
 * normalizer and the runner's decision-to-action conversion alike).
 */
export function normalizeModelActionVerb(type: string): string {
  const verb = type.trim()
  return verb.toLowerCase() === 'fill' ? 'type' : verb
}
